import React, { useState } from 'react';
import { ChannelFormData, SchemaProperty, Secret, Mediator } from '../types';
import { UseRequestSchemaReturn } from '../hooks/useRequestSchema';

export interface InboundTabProps {
  // Form data and state
  formData: ChannelFormData;
  setFormData: React.Dispatch<React.SetStateAction<ChannelFormData>>;
  setIsModified: React.Dispatch<React.SetStateAction<boolean>>;
  
  secrets: Secret[];
  mediators: Mediator[];
  openSecretsDropdown: number | null;
  setOpenSecretsDropdown: React.Dispatch<React.SetStateAction<number | null>>;
  
  // Shared handlers
  handleInputChange: (field: keyof ChannelFormData, value: string | boolean) => void;
  handleSchemaChange: (value: string, isResponse?: boolean, isMetadata?: boolean) => void;
  
  // Request schema hook (contains all schema-related state and functions)
  requestSchema: UseRequestSchemaReturn;
}

const InboundTab: React.FC<InboundTabProps> = ({
  formData,
  setFormData,
  setIsModified,
  secrets,
  mediators,
  openSecretsDropdown,
  setOpenSecretsDropdown,
  handleInputChange,
  handleSchemaChange,
  requestSchema,
}) => {
  const [identityVerificationCollapsed, setIdentityVerificationCollapsed] = useState(true);
  
  // Destructure all schema-related functions and state from the hook
  const {
    schemaProperties,
    setSchemaProperties,
    schemaJson,
    schemaJsonValid,
    schemaValidationErrors,
    showRequestJson,
    setShowRequestJson,
    metadataSchemaProperties,
    setMetadataSchemaProperties,
    metadataSchemaJson,
    setMetadataSchemaJson,
    metadataSchemaJsonValid,
    metadataSchemaValidationErrors,
    showMetadataJson,
    setShowMetadataJson,
    togglePropertyCollapsed,
    isChildVisible,
    addSchemaProperty,
    addChildProperty,
    removeProperty,
    updatePropertyName,
    updatePropertyType,
    updatePropertyRequired,
    updatePropertyXIdentity,
    updateValidationRuleType,
    updateValidationRuleValue,
    updateValidationRuleElementType,
    updateValidationRuleValues,
    updateValidationRuleMin,
    updateValidationRuleMax,
    updateSchemaJsonFromProperties,
    updateSchemaJson,
    parseSchemaPreservingCollapsedState,
    expandAllProperties,
    collapseAllProperties,
    syncVisualToJson,
    syncJsonToVisual,
    syncRequestVisualWithJson,
    addMetadataProperty,
    addMetadataChildProperty,
    removeMetadataProperty,
    updateMetadataPropertyName,
    updateMetadataPropertyType,
    updateMetadataPropertyValue,
    toggleMetadataPropertyCollapsed,
    isMetadataChildVisible,
    updateMetadataJsonFromProperties,
    syncMetadataVisualToJson,
    syncMetadataJsonToVisual,
    parseValueType,
  } = requestSchema;

  return (
    <>
      {/* <p className="text-muted mb-3">
        Validation rules and schema requests coming in to the Gateway before processing the request.
      </p> */}

    {/* How it works */}
    <div className="card shadow-sm mb-4">
      <div className="card-header bg-light" style={{ cursor: 'pointer' }} onClick={() => setIdentityVerificationCollapsed(!identityVerificationCollapsed)}>
        <div className="d-flex justify-content-between align-items-center">
          <h6 className="m-0 font-weight-bold text-muted">
            <i className="fas fa-graduation-cap"></i> <i>How Identity works with the Trust Gateway</i>
          </h6>
          <i className={`fas fa-chevron-${identityVerificationCollapsed ? 'down' : 'up'}`}></i>
        </div>
      </div>
      {!identityVerificationCollapsed && (
      <div className="card-body">
        
        <div className="alert alert-info mb-3">
            <i className="fas fa-info-circle"></i>
            <strong className='text-nowrap'>Key Insight:</strong> This section will help you understand how identity creation, extraction, and verification works when requests flow through the Trust Gateway, especially in Gateway-to-Gateway scenarios. When one Trust Gateway forwards a request to another Trust Gateway, the sending Gateway may be configured to inject a cryptographic identity proof called a Verifiable Presentation (VP). If this is present in the request received by this Gateway, it will be automatically processed.<br/><br/>The Identity Creation process is not involved if the request already contains a VP, it is verified and used.
        </div>

        <div className="card bg-light border-0">
          <div className="card-body">
            <h6 className="font-weight-bold text-dark mb-3">
              <i className="fas fa-shield-alt mr-2"></i>
              How agents identify themselves to the Gateway
            </h6>

            <p className="mb-3 text-gray-800">
              When agents communicate with each other, there is no single standard that is used to uniquely identify an agent, no long-term, durable, consistend identifer that can be 
              used to know that a subsequent request is coming from the same agent. Enterprise agents may use Identity Provider (IdP) frameworks to provide identities to agents,
              but there is a problem here - to resolve the identity, the receiving agent needs to communicate with the IdP, which may not be preferable in all scenarios because to trust
              the IdP, you need to make network calls which can be monitored and logged, and is not a privacy-preserving approach. We need to do better, in a more decentralised way!
              </p>

<p className="mb-3 text-gray-800">
              The better approach is for all agents to have their own Decentralised Identifier (DID), which is a cryptographic identifier that is owned 
              and controlled by the agent itself (or the developer/enterprise that owns it). This DID can be used to create Verifiable Credentials (VCs) and Verifiable Presentations (VPs)
              to allow the agent to make attestations about itself or its owner. And the great thing is that by using decentralised identifiers, we do not have to trust
              a centralised Identity Provider - the agent can prove its identity cryptographically, without needing to make network calls to an external service.
</p>
<p className="mb-3 text-gray-800">
              That all sounds great! But the problem is that not every developer has the ability to make use of decentralised technology - it is complex and requires a lot of knowledge to do properly.
              Luckily, this is where the Affinidi Trust Fabric Gateway comes in - it acts as a bridge between traditional agents and decentralised identity. When an agent communicates
              with the Gateway, it can simply provide some basic identity information in the request metadata, and the Gateway will take care of creating a durable
              decentralised identity for the agent behind the scenes. This way, even if the agent does not understand decentralised identity, it can still benefit from it.
            </p>

            <h6 className="font-weight-bold text-dark mb-3 mt-4">
              <i className="fas fa-project-diagram mr-2"></i>
              Identities for agents
            </h6>
            <p>We mentioned earlier that we need some 'basic identity information' passed in from the agent when it makes a call to the Gateway,
              and we can then mine this information to generate an identity for the agent. But what exactly is this 'basic identity information'?
            </p>
            <p>
              The good news is that it can be... anything! The developer gets to decide what properties will uniquely identify a particular agent. It could
              be a simple name, or it could be a combination of properties like name, version, organization, environment, etc. The more unique the combination of properties, the better 
              because thee is less chance of two different agents ending up with the same identity. Note that the developer should strive to never
              allow two different agents to end up with the same identity.
            </p>

            <p>Different types of agents provide their identity information in different ways:</p>
            <p className="mb-3 text-gray-800">
              <strong>MCP (Model Context Protocol) Agents:</strong> These agents typically include identity information directly in their request metadata. 
              For example, an MCP client might send an <code style={{backgroundColor: '#e9ecef', padding: '2px 6px', borderRadius: '3px'}}>agentIdentity</code> field 
              containing details like <code style={{backgroundColor: '#e9ecef', padding: '2px 6px', borderRadius: '3px'}}>agentName</code>, 
              <code style={{backgroundColor: '#e9ecef', padding: '2px 6px', borderRadius: '3px'}}>agentVersion</code>, and 
              <code style={{backgroundColor: '#e9ecef', padding: '2px 6px', borderRadius: '3px'}}>agentOrganization</code>. This allows the Gateway to know exactly 
              which agent is making the request and apply appropriate policies or route the request accordingly.
            </p>

            <p className="mb-3 text-gray-800">
              <strong>A2A (Agent-to-Agent) Protocol:</strong> In A2A communications, agents use an A2A Extension Point (which the specification supports) 
              to include additional metadata in their requests that the Gateway can use to map the agent identity. Again, the fields
              can be anything the developer decides, but common choices include <code style={{backgroundColor: '#e9ecef', padding: '2px 6px', borderRadius: '3px'}}>agentName</code>,
              <code style={{backgroundColor: '#e9ecef', padding: '2px 6px', borderRadius: '3px'}}>agentVersion</code>, and 
              <code style={{backgroundColor: '#e9ecef', padding: '2px 6px', borderRadius: '3px'}}>agentOrganization</code>. 
            </p>

            <h6 className="font-weight-bold text-dark mb-3 mt-4">
              <i className="fas fa-project-diagram mr-2"></i>
              Deciding what identity properties to use
            </h6>
            <p>Sometimes too much choice is a bad thing! So the Gateway provides some examples you can
              use as a starting point when deciding which properties to use for identity creation. See the help documentation
              for a walkthrough of some common scenarios and recommended property sets.
            </p>

            <h6 className="font-weight-bold text-dark mb-3 mt-4">
              <i className="fas fa-project-diagram mr-2"></i>
              Configuring the Gateway for identity extraction
            </h6>
            <p>Once you have decided what properties to use for identity creation, you need to configure the Gateway to know where to find these properties. This is done
              by creating a schema that describes the structure of the identity information in the request metadata. You can use the visual schema editor in the
              Channel editor to create this schema. The schema should match the structure of the identity information sent by the agents. You can also 
              edit the JSON schema directly by hand if you prefer.
            </p>
            <p>
              But creating the schema manually is cumbersome and error prone, so the Gateway provides an Onboarding system that can automatically generate the schema for you based on sample requests.
              This makes it really easy to configure the Gateway for identity extraction - simply capture some sample requests from your agents, and let the Gateway generate the schema for you! You can
              then use the visual editor or JSON editor to tweak the schema if needed.
            </p>

            <h6 className="font-weight-bold text-dark mb-3 mt-4">
              <i className="fas fa-project-diagram mr-2"></i>
              Single Gateway Identity Flow
            </h6>
            <p className="mb-2 text-gray-700">Now we know why we need to extract an identity, and how we configure it, the next step is to 
              think about how the flows work. Let's start with a simple single Gateway scenario.
            </p>
            <p className="mb-2 text-gray-700">Say you configure a Channel and enable identity extraction, and define a schema that extracts
              a unique identity. When an agent makes a request to the Gateway through this Channel, the specified agent identity field
              will be parsed, and then the various fields that you configured in the schema will be extracted. The Gateway will then determine if this is a brand new
              identity that it has never seen before, or if it matches an existing identity. If it is a new identity, the Gateway will create a new Decentralised Identifier (DID) for the agent, and store the extracted properties
              for future matching for that same agent. If it matches an existing identity, the Gateway will simply use the existing DID for that agent.
            </p>
            <p className="mb-2 text-gray-700">Once the Gateway has the DID for the agent, it will use the keys that it has access to, to 
              create a Verifiable Credential (VC) containing all the fields that were extracted, and then create a Verifiable Presentation (VP) that contains the agent's DID and the VC with the extracted properties. This VP is then
              added to the request metadata, and the request is processed as normal by the Gateway. 
            </p>
            <p className="mb-2 text-gray-700">As this is a single Gateway scenario, typically the next step is for the Gateway to send the request to the target endpoint,
              and the request will now contain the VP that cryptographically proves the identity of the agent that made the request. The target endpoint can then verify the VP
              to ensure that the agent is who they say they are.
            </p>


            <h6 className="font-weight-bold text-dark mb-3 mt-4">
              <i className="fas fa-project-diagram mr-2"></i>
              Gateway-to-Gateway Identity Flow
            </h6>
            <p className="mb-2 text-gray-700">Ok, that was a simple example - it gets more complicated when we have one Gateway forwarding a request across the trust boundary
              to another Gateway. In this case, the same identity extraction and VP injection occurs in the first Gateway (GW1) and the request is forwarded via 
              the DIDComm v2.1 protocol to the second Gateway (GW2) via the Connection Point that has been established.</p>
              <p>But when GW2 receives the request, instead of it containing the raw identity fields that GW1 saw, it contains the VP that GW1 has injected. The flow goes like this:</p>
            <ol className="mb-3 text-gray-800">
              <li className="mb-2"><strong>GW1 (Sender):</strong> Creates and signs a VP containing the caller's identity, then injects it into the forwarded request</li>
              <li className="mb-2"><strong>GW2 (Receiver):</strong> Detects the VP in the request metadata and automatically attempts to verify it</li>
              <li className="mb-2"><strong>Cryptographic Verification:</strong> GW2 validates the VP's signature using GW1's public key from its DID document</li>
              <li className="mb-2"><strong>Storage:</strong> GW2 stores the identity with a <code style={{backgroundColor: '#e9ecef', padding: '2px 6px', borderRadius: '3px', color: '#d63384'}}>verified</code> flag indicating whether the signature was valid</li>
            </ol>

            <h6 className="font-weight-bold text-dark mb-3 mt-4">
              <i className="fas fa-check-double mr-2"></i>
              Verification Status Meanings
            </h6>
            <div className="table-responsive mb-3">
              <table className="table table-sm table-bordered mb-0">
                <thead className="thead-light">
                  <tr>
                    <th style={{width: '30%'}}>Status</th>
                    <th>Meaning</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <span className="badge badge-success">
                        <i className="fas fa-check-circle mr-1"></i>
                        verified: true
                      </span>
                    </td>
                    <td>The VP signature was cryptographically verified. GW2 trusts that this identity was authenticated by GW1.</td>
                  </tr>
                  <tr>
                    <td>
                      <span className="badge badge-warning">
                        <i className="fas fa-exclamation-triangle mr-1"></i>
                        verified: false
                      </span>
                    </td>
                    <td>VP verification failed or no VP was present. The identity exists but hasn't been cryptographically proven.</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h6 className="font-weight-bold text-dark mb-3 mt-4">
              <i className="fas fa-cog mr-2"></i>
              What this Identity Creation Configuration Actually Controls
            </h6>
            <p className="mb-2 text-gray-700">
              Now we understand how identity is extracted and verified, it's important to clarify what the settings in this section actually do. Enabling or disabling identity creation here
              controls whether GW1 attempts to extract identity fields from incoming requests and create a new identity when no previous identity is known to the Gateway for that
              combination of properties. If this setting is disabled, GW1 will NOT attempt to extract identity fields from incoming requests, and therefore will not create new identities for previously unknown agents.
              The raw identity information that was passed in via the Extensision Point or MCP metadata will simply be sent to the receiving Gateway (GW2).
            </p>
            <p>
              If identity extraction/creation is enabled on GW2, it will perform the same extraction process on the raw identity data - exactly as GW1 would have done.
            </p>
            <p>
              However, this setting does NOT affect whether VP verification occurs - VP verification always happens automatically when a VP is present in a request. Disabling identity creation here simply prevents the Gateway from attempting to extract identity fields from requests
              that do not already contain a VP. In other words, if an incoming request already has a VP, the Gateway will always verify it regardless of this setting. This setting only controls whether
              the Gateway tries to create a new identity when no VP is present.
            </p>
            <div className="alert alert-warning mb-0">
                <i className="fas fa-exclamation-triangle mr-2 text-warning"></i>
                <strong>Configuration Note:</strong> Disabling identity on a Gateway does NOT prevent VP verification if a VP is present in the request. It only prevents fallback identity extraction when no VP is present.
            </div>

                        <h6 className="font-weight-bold text-dark mb-3 mt-4">
              <i className="fas fa-cog mr-2"></i>
              Alternative configuration - Agent owns the Identity (and keys)
            </h6>
            <p className="mb-2 text-gray-700">
              using the Affinidi Trust Developer Kit (TDK), agents can create and own their own Decentralised Identifiers (DIDs) and associated cryptographic keys. In this scenario, the agent is responsible for generating
              and managing its own identity, rather than relying on the Gateway to create and manage it. When the agent makes a request to the Gateway, it includes its DID and a Verifiable Presentation (VP) signed with its own keys.
            </p>
            <p className="mb-2 text-gray-700">
              The Gateway then verifies the VP using the agent's DID document, which contains the public keys needed for verification. This approach provides a higher level of security and decentralization, as the agent has full control over its identity and keys.
              However, it also requires the agent to have the capability to manage its own identity and keys, which may not be feasible for all use cases. And this
              last point is exactly why the Gateway-based identity creation approach exists - to simplify identity management for agents that cannot or do not want to manage their own identities.
            </p>
          </div>
        </div>
      </div>
      )}
    </div>

      
      {/* Identity Extraction Section */}
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-light">
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="m-0 font-weight-bold text-primary"><i className="fas fa-fingerprint"></i> Identity Creation</h6>
            <div className="d-flex align-items-center gap-3">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="identity-enabled"
                  checked={(formData.protocol === 'mcp' || formData.protocol === 'mpx') ? (formData.mcp_identity_config?.enabled || false) : formData.enforce_extension}
                  onChange={(e) => {
                    if (formData.protocol === 'mcp' || formData.protocol === 'mpx') {
                      setFormData({
                        ...formData,
                        mcp_identity_config: {
                          ...formData.mcp_identity_config,
                          enabled: e.target.checked,
                          meta_field: formData.mcp_identity_config?.meta_field || 'agentIdentity'
                        }
                      });
                    } else {
                      handleInputChange('enforce_extension', e.target.checked);
                    }
                    setIsModified(true);
                  }}
                />
                <label className="form-check-label" htmlFor="identity-enabled" style={{ color: ((formData.protocol === 'mcp' || formData.protocol === 'mpx') ? (formData.mcp_identity_config?.enabled || false) : formData.enforce_extension) ? '#000' : '#6c757d' }}>
                  Enabled
                </label>
              </div>
            </div>
          </div>
        </div>
        {((formData.protocol === 'mcp' || formData.protocol === 'mpx') ? (formData.mcp_identity_config?.enabled || false) : formData.enforce_extension) && (
        <div className="card-body">
          {/* Identity Meta Field Configuration */}
          <div className="mb-4">
            <label htmlFor="channel-edit-mcp-meta-field" className="form-label fw-bold">
              Identity Meta Field Name
            </label>
            <input
              type="text"
              className="form-control"
              id="channel-edit-mcp-meta-field"
              value={formData.mcp_identity_config?.meta_field || 'agentIdentity'}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  mcp_identity_config: {
                    ...formData.mcp_identity_config,
                    enabled: formData.mcp_identity_config?.enabled || false,
                    meta_field: e.target.value
                  }
                });
                setIsModified(true);
              }}
              placeholder="agentIdentity"
            />
            <small className="form-text text-muted">
              The field name within <code>_meta</code> that contains the agent identity object (e.g., <code>_meta.agentIdentity</code>)
            </small>
          </div>
    
    {/* Visual Schema Editor */}
    <div className="mb-3">
      <label className="form-label fw-bold">Request JSON Schema Properties</label>
      <div className="mb-2">
        <button
          style={{ marginRight: "10px" }}
          type="button"
          className="btn btn-sm btn-primary me-3"
          onClick={() => {
            setSchemaProperties(prev => prev.map(p => ({ ...p, collapsed: false })));
          }}
        >
          Expand All
        </button>
        <button
          type="button"
          className="btn btn-sm btn-primary ms-3"
          onClick={() => {
            setSchemaProperties(prev => prev.map(p => ({ ...p, collapsed: true })));
          }}
        >
          Collapse All
        </button>
      </div>
      <div id="schema-properties-list" className="mb-2">
        {schemaProperties.map((property, index) => {
          // Check if this property should be visible (not hidden by collapsed parent)
          if (property.level > 0 && !isChildVisible(property, property.parent)) {
            return null;
          }
          
          return (
          <div key={`schema-prop-${index}`} className="schema-property-card" data-level={property.level}>
            <div className="card-body">
              <div className="d-flex align-items-center gap-2 mb-2">
                {property.type === 'object' && (
                  <button
                    type="button"
                    className="btn btn-sm btn-info"
                    onClick={() => togglePropertyCollapsed(index)}
                    title={property.collapsed ? "Expand children" : "Collapse children"}
                  >
                    {property.collapsed ? '↓' : '↑'}
                  </button>
                )}
                <input
                  type="text"
                  className="form-control"
                  placeholder="Property name"
                  value={property.name}
                  data-property-index={index.toString()}
                  data-field-type="name"
                  onChange={(e) => updatePropertyName(index, e.target.value)}
                  onBlur={() => updateSchemaJson()}
                />
                <select
                  className="form-control property-type-select"
                  value={property.type}
                  data-property-index={index.toString()}
                  data-field-type="type"
                  onChange={(e) => updatePropertyType(index, e.target.value)}
                  onBlur={() => updateSchemaJson()}
                  title="Property type"
                >
                  <option value="string">String</option>
                  <option value="number">Number</option>
                  <option value="boolean">Boolean</option>
                  <option value="object">Object</option>
                  <option value="array">Array</option>
                </select>
                {property.type === 'object' && (
                  <button
                    type="button"
                    className="btn btn-sm btn-success"
                    onClick={() => addChildProperty(index)}
                    title="Add child property"
                  >
                    <i className="fas fa-plus"></i>
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-sm btn-danger"
                  onClick={() => removeProperty(index)}
                  title="Remove property"
                >
                  <i className="fas fa-trash"></i>
                </button>
              </div>
              
              <div className="d-flex align-items-center gap-5">
                {['string', 'number', 'boolean'].includes(property.type) && (
                  <>
                    <div className="form-check me-4" title="Include in identity hash computation" style={{ marginRight: '10px' }}>
                      <input
                        type="checkbox"
                        className="form-check-input"                                
                        id={`identity-${index}`}
                        checked={property.xIdentity}
                        onChange={(e) => updatePropertyXIdentity(index, e.target.checked)}
                        onBlur={() => updateSchemaJson()}
                      />
                      <label className="form-check-label" htmlFor={`identity-${index}`}>Identity
                      </label>
                    </div>
                    <div className="form-check" title={property.xIdentity ? "Identity fields are automatically required" : "Property is required"}>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id={`required-${index}`}
                        checked={property.required}
                        disabled={property.xIdentity}
                        onChange={(e) => updatePropertyRequired(index, e.target.checked)}
                        onBlur={() => updateSchemaJson()}
                      />
                      <label className="form-check-label" htmlFor={`required-${index}`}>Required</label>
                    </div>
                  </>
                )}
              </div>
              
              {/* Validation Rules */}
              {(['string', 'number'].includes(property.type) || property.type === 'array') && (
                <div className="mt-2">
                  <div className="row">
                    <div className="col-md-4">
                      <select
                        className="form-control form-control-sm"
                        value={property.validationRule?.type || ''}
                        data-property-index={index.toString()}
                        data-field-type="validation-type"
                        onChange={(e) => updateValidationRuleType(index, e.target.value)}
                        onBlur={() => updateSchemaJson()}
                        title="Validation rule type"
                      >
                        <option value="">No validation</option>
                        {property.type !== 'array' && (
                          <>
                            <option value="equals">Equals</option>
                            <option value="contains">Contains</option>
                            {property.type !== 'number' && <option value="regex">Regex</option>}
                            {property.type === 'number' && <option value="range">Range</option>}
                          </>
                        )}
                        {property.type === 'array' && (
                          <>
                            <option value="array-all">Array All Elements</option>
                            <option value="array-any">Array Any Element</option>
                            <option value="array-length">Array Length</option>
                          </>
                        )}
                      </select>
                    </div>
                    {property.validationRule && (
                      <div className="col-md-8">
                        {!property.validationRule.type.startsWith('array-') ? (
                          <>
                            <input
                              type="text"
                              className={`form-control form-control-sm ${
                                property.validationRule.type === 'regex' && property.validationRule.value 
                                  ? ((() => {
                                      try {
                                        new RegExp(property.validationRule.value);
                                        return 'is-valid';
                                      } catch (e) {
                                        return 'is-invalid';
                                      }
                                    })()
                                  ) : ''
                              }`}
                              placeholder={
                                property.validationRule.type === 'equals' ? 'Enter expected value...' :
                                property.validationRule.type === 'contains' ? 'Enter text that must be contained...' :
                                property.validationRule.type === 'regex' ? 'Enter regex pattern (e.g., ^[A-Z]+$)...' :
                                property.validationRule.type === 'range' ? 'Enter range as min-max (e.g., 1-100 or -100 or 1-)...' :
                                'Validation value'
                              }
                              value={property.validationRule.value || ''}
                              data-property-index={index.toString()}
                              data-field-type="validation-value"
                              onChange={(e) => updateValidationRuleValue(index, e.target.value)}
                              onBlur={() => updateSchemaJson()}
                            />
                            <div className="form-text text-muted" style={{fontSize: '0.75rem', marginTop: '2px'}}>
                              {property.validationRule.type === 'equals' && 'Field must exactly match this value'}
                              {property.validationRule.type === 'contains' && 'Field must contain this text'}
                              {property.validationRule.type === 'regex' && (
                                property.validationRule.value ? (
                                  (() => {
                                    try {
                                      new RegExp(property.validationRule.value);
                                      return <span className="text-success"><i className="fas fa-check"></i> Valid regex pattern</span>;
                                    } catch (e) {
                                      return <span className="text-danger"><i className="fas fa-times"></i> Invalid regex pattern</span>;
                                    }
                                  })()
                                ) : 'Enter a JavaScript-compatible regular expression'
                              )}
                              {property.validationRule.type === 'range' && 'Format: min-max (leave min or max empty for unbounded, e.g., "-100" for max 100)'}
                            </div>
                          </>
                        ) : (
                          <>
                            {/* Array validation inputs */}
                            {(property.validationRule.type === 'array-all' || property.validationRule.type === 'array-any') && (
                              <div className="mb-2">
                                <label className="form-label form-label-sm">Element Type</label>
                                <select
                                  title="Validation type"
                                  className="form-control form-control-sm"
                                  value={property.validationRule.elementType || 'string'}
                                  data-property-index={index.toString()}
                                  data-field-type="validation-element-type"
                                  onChange={(e) => updateValidationRuleElementType(index, e.target.value)}
                                  onBlur={() => updateSchemaJson()}
                                >
                                  <option value="string">String</option>
                                  <option value="number">Number</option>
                                  <option value="boolean">Boolean</option>
                                  <option value="object">Object</option>
                                  <option value="array">Array</option>
                                </select>
                              </div>
                            )}
                            {(property.validationRule.type === 'array-all' || property.validationRule.type === 'array-any') && (
                              <div className="mb-2">
                                <label className="form-label form-label-sm">Allowed Values (optional)</label>
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  placeholder="Comma-separated values (e.g., value1, value2, value3)"
                                  value={property.validationRule.values || ''}
                                  data-property-index={index.toString()}
                                  data-field-type="validation-values"
                                  onChange={(e) => updateValidationRuleValues(index, e.target.value)}
                                  onBlur={() => updateSchemaJson()}
                                />
                              </div>
                            )}
                            {property.validationRule.type === 'array-length' && (
                              <div className="row">
                                <div className="col-6">
                                  <label className="form-label form-label-sm">Min Length</label>
                                  <input
                                    type="number"
                                    className="form-control form-control-sm"
                                    placeholder="Min"
                                    min="0"
                                    value={property.validationRule.min || ''}
                                    data-property-index={index.toString()}
                                    data-field-type="validation-min"
                                    onChange={(e) => updateValidationRuleMin(index, e.target.value)}
                                    onBlur={() => updateSchemaJson()}
                                  />
                                </div>
                                <div className="col-6">
                                  <label className="form-label form-label-sm">Max Length</label>
                                  <input
                                    type="number"
                                    className="form-control form-control-sm"
                                    placeholder="Max"
                                    min="0"
                                    value={property.validationRule.max || ''}
                                    data-property-index={index.toString()}
                                    data-field-type="validation-max"
                                    onChange={(e) => updateValidationRuleMax(index, e.target.value)}
                                    onBlur={() => updateSchemaJson()}
                                  />
                                </div>
                              </div>
                            )}
                            <div className="form-text text-muted" style={{fontSize: '0.75rem', marginTop: '2px'}}>
                              {property.validationRule.type === 'array-all' && 'All array elements must match the specified type and values'}
                              {property.validationRule.type === 'array-any' && 'At least one array element must match the specified type and values'}
                              {property.validationRule.type === 'array-length' && 'Array must have length within the specified range'}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          );
        })}
      </div>
      <button
        type="button"
        className="btn btn-sm btn-success mt-2"
        onClick={addSchemaProperty}
      >
        <i className="fas fa-plus"></i> Add Root Property
      </button>
    </div>

    <div className="mb-3">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <label className="form-label fw-bold mb-0 d-flex align-items-center gap-2">
          Request Schema Raw JSON
          {!schemaJsonValid && (
            <span className="badge badge-danger ml-2">
              <i className="fas fa-exclamation-triangle"></i> Invalid JSON
            </span>
          )}
          {schemaJsonValid && schemaJson.trim() && (
            <span className="badge badge-success ml-2">
              <i className="fas fa-check"></i> Valid JSON
            </span>
          )}
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={syncRequestVisualWithJson}
            disabled={!schemaJsonValid}
            title="Sync visual editor with JSON changes"
          >
            <i className="fas fa-sync"></i> Sync Visual
          </button>
        </label>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={() => setShowRequestJson(!showRequestJson)}
          title={showRequestJson ? "Hide JSON" : "Show JSON"}
        >
          <i className={`fas fa-${showRequestJson ? 'eye-slash' : 'eye'}`}></i> {showRequestJson ? 'Hide' : 'Show'} JSON
        </button>
      </div>
      {showRequestJson && (
      <div className={`schema-property-card ${!schemaJsonValid ? 'border-danger' : schemaJsonValid && schemaJson.trim() ? 'border-success' : ''}`}>
        <textarea 
          className={`form-control schema-raw-json ${!schemaJsonValid ? 'is-invalid' : schemaJsonValid && schemaJson.trim() ? 'is-valid' : ''}`}
          id="schema-raw-json"
          rows={15}
          value={schemaJson}
          onChange={(e) => handleSchemaChange(e.target.value, false)}
          onInput={(e) => handleSchemaChange((e.target as HTMLTextAreaElement).value, false)}
          onKeyUp={(e) => handleSchemaChange((e.target as HTMLTextAreaElement).value, false)}
          placeholder="Edit JSON schema directly..."
        />
        {!schemaJsonValid && (
          <div className="invalid-feedback d-block">
            <i className="fas fa-exclamation-triangle"></i> 
            {schemaValidationErrors.length > 0 ? (
              <div>
                {schemaValidationErrors.map((error, idx) => (
                  <div key={idx}>{error}</div>
                ))}
              </div>
            ) : (
              'Invalid JSON'
            )}
          </div>
        )}
      </div>
      )}
    </div>

    
    </div>
    )}
    </div>

    
    {/* Injection Section */}
    <div className="card shadow-sm mb-4">
      <div className="card-header bg-light">
        <div className="d-flex justify-content-between align-items-center">
          <h6 className="m-0 font-weight-bold text-primary">
            <i className="fas fa-syringe"></i> Custom Metadata Injection
          </h6>
          <div className="d-flex align-items-center gap-3">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="injection-enabled"
                checked={formData.custom_metadata?.enabled || false}
                onChange={(e) => {
                  setIsModified(true);
                  setFormData(prev => ({
                    ...prev,
                    custom_metadata: {
                      ...prev.custom_metadata,
                      enabled: e.target.checked
                    }
                  }));
                }}
              />
              <label className="form-check-label" htmlFor="injection-enabled" style={{ color: (formData.custom_metadata?.enabled || false) ? '#000' : '#6c757d' }}>
                Enabled
              </label>
            </div>
          </div>
        </div>
      </div>
      {(formData.custom_metadata?.enabled || false) && (
      <div className="card-body">
        <p className="text-muted mb-3">
          Configure custom metadata injection for this channel. This metadata is automatically injected by the Gateway and can be used by the target to make decisions at runtime.
        </p>
        
        {formData.custom_metadata?.enabled && (
          <>
            {/* MCP Injection Target Selection */}
            {(formData.protocol === 'mcp' || formData.protocol === 'mpx') && (
              <div className="mb-4 p-3 bg-light border rounded">
                <label className="form-label fw-bold mb-2">
                  <i className="fas fa-bullseye text-primary me-2"></i> Metadata Injection Target</label>
                <p className="text-muted small mb-3">
                  Choose where to inject metadata for MCP requests. <code>_meta</code> field is protocol-native and transport-agnostic. 
                  HTTP headers are transport-level only. Use <strong>both</strong> for maximum compatibility.
                </p>
                <div className="d-flex gap-3">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="injection_target_request"
                      id="injection-meta-request"
                      value="meta"
                      checked={formData.custom_metadata?.injection_target === 'meta'}
                      onChange={(e) => {
                        setIsModified(true);
                        setFormData(prev => ({
                          ...prev,
                          custom_metadata: {
                            ...prev.custom_metadata,
                            injection_target: 'meta'
                          }
                        }));
                      }}
                    />
                    <label className="form-check-label" htmlFor="injection-meta-request">
                      <code>_meta</code> field only
                      <small className="d-block text-muted">JSON-RPC protocol-level</small>
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="injection_target_request"
                      id="injection-headers-request"
                      value="headers"
                      checked={formData.custom_metadata?.injection_target === 'headers'}
                      onChange={(e) => {
                        setIsModified(true);
                        setFormData(prev => ({
                          ...prev,
                          custom_metadata: {
                            ...prev.custom_metadata,
                            injection_target: 'headers'
                          }
                        }));
                      }}
                    />
                    <label className="form-check-label" htmlFor="injection-headers-request">
                      HTTP headers only
                      <small className="d-block text-muted">Transport-level (HTTP only)</small>
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="injection_target_request"
                      id="injection-both-request"
                      value="both"
                      checked={!formData.custom_metadata?.injection_target || formData.custom_metadata?.injection_target === 'both'}
                      onChange={(e) => {
                        setIsModified(true);
                        setFormData(prev => ({
                          ...prev,
                          custom_metadata: {
                            ...prev.custom_metadata,
                            injection_target: 'both'
                          }
                        }));
                      }}
                    />
                    <label className="form-check-label" htmlFor="injection-both-request">
                      <strong>Both</strong> (recommended)
                      <small className="d-block text-muted">Maximum compatibility</small>
                    </label>
                  </div>
                </div>
              </div>
            )}
            
            <div className="mb-3">
              <label className="form-label fw-bold">Channel Metadata Properties (Visual Editor)</label>
              <p className="text-muted small">Define key-value pairs for your channel metadata. Values can be nested objects, arrays, strings, numbers, or booleans.</p>
              <div className="mb-2">
                <button
                  type="button"
                  className="btn btn-sm btn-success"
                  onClick={addMetadataProperty}
                >
                  <i className="fas fa-plus"></i> Add Property
                </button>
              </div>
              <div id="metadata-properties-list" className="mb-2">
                {metadataSchemaProperties.map((property, index) => {
                  // Check if this property should be visible (not hidden by collapsed parent)
                  if (property.level > 0 && !isMetadataChildVisible(property, property.parent)) {
                    return null;
                  }
                  
                  return (
                    <div key={`metadata-prop-${index}`} className="schema-property-card mb-2" data-level={property.level}>
                      <div className="card-body p-2">
                        <div className="d-flex align-items-center gap-2 mb-2">
                          {property.type === 'object' && (
                            <button
                              type="button"
                              className="btn btn-sm btn-info"
                              onClick={() => toggleMetadataPropertyCollapsed(index)}
                              title={property.collapsed ? "Expand children" : "Collapse children"}
                            >
                              {property.collapsed ? '↓' : '↑'}
                            </button>
                          )}
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Property name"
                            value={property.name}
                            style={{ maxWidth: '200px' }}
                            onChange={(e) => {
                              const newProps = [...metadataSchemaProperties];
                              newProps[index].name = e.target.value;
                              setMetadataSchemaProperties(newProps);
                              setIsModified(true);
                            }}
                            onBlur={() => updateMetadataJsonFromProperties()}
                          />
                          {property.type !== 'object' && (
                            <div className="d-flex gap-2 flex-grow-1">
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder='Value (e.g., "text", 123, true, [1,2,3]) or $SECRET:secret_name'
                                value={(property as any).value || ''}
                                onChange={(e) => {
                                  const newProps = [...metadataSchemaProperties];
                                  const { type } = parseValueType(e.target.value);
                                  (newProps[index] as any).value = e.target.value;
                                  newProps[index].type = type;
                                  setMetadataSchemaProperties(newProps);
                                  setIsModified(true);
                                }}
                                onBlur={() => updateMetadataJsonFromProperties()}
                              />
                              <div className="dropdown" style={{ position: 'relative' }}>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={() => setOpenSecretsDropdown(openSecretsDropdown === index ? null : index)}
                                  title="Select secret"
                                >
                                  <i className="fas fa-key"></i>
                                </button>
                                {openSecretsDropdown === index && (
                                <ul className="dropdown-menu dropdown-menu-end show" style={{ position: 'absolute', right: 0, top: '100%', zIndex: 1000 }}>
                                  {secrets.length === 0 ? (
                                    <li><span className="dropdown-item-text text-muted">No secrets available</span></li>
                                  ) : (
                                    secrets.map((secret) => (
                                      <li key={secret.id}>
                                        <button
                                          type="button"
                                          className="dropdown-item"
                                          onClick={() => {
                                            const newProps = [...metadataSchemaProperties];
                                            (newProps[index] as any).value = `$SECRET:${secret.secret_id}`;
                                            newProps[index].type = 'string';
                                            setMetadataSchemaProperties(newProps);
                                            setIsModified(true);
                                            updateMetadataJsonFromProperties();
                                            setOpenSecretsDropdown(null);
                                          }}
                                        >
                                          <div>
                                            <strong>{secret.name}</strong>
                                            <div className="text-muted small">ID: {secret.secret_id}</div>
                                            {secret.description && (
                                              <div className="text-muted small">{secret.description}</div>
                                            )}
                                          </div>
                                        </button>
                                      </li>
                                    ))
                                  )}
                                </ul>
                                )}
                              </div>
                            </div>
                          )}
                          <button
                            type="button"
                            className="btn btn-sm btn-success"
                            onClick={() => addMetadataChildProperty(index)}
                            title="Add nested property"
                          >
                            <i className="fas fa-plus"></i>
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            onClick={() => removeMetadataProperty(index)}
                            title="Remove property"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                        {property.type !== 'object' && (property as any).value && (
                          <div className="text-muted small">
                            Type: <span className="badge badge-info">{property.type}</span>
                            {((property as any).value as string)?.startsWith('$SECRET:') && (
                              <span className="badge badge-warning ml-2">
                                <i className="fas fa-key"></i> Secret
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            
            <div className="mb-3">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <label className="form-label fw-bold mb-0 d-flex align-items-center gap-2">
                  <span>
                    Extension Metadata JSON (Direct Editor)
                    {!metadataSchemaJsonValid && (
                      <span className="text-danger ml-2">
                        <i className="fas fa-exclamation-triangle"></i> Invalid JSON
                      </span>
                    )}
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => {
                    // Sync visual editor from JSON
                    try {
                      const parsed = JSON.parse(metadataSchemaJson);
                      const props: SchemaProperty[] = [];
                      
                      const parseObject = (obj: any, level: number, parentIndex?: number) => {
                        Object.keys(obj).forEach(key => {
                          const val = obj[key];
                          let type = 'string';
                          let valueStr = '';
                          
                          if (typeof val === 'number') {
                            type = 'number';
                            valueStr = String(val);
                          } else if (typeof val === 'boolean') {
                            type = 'boolean';
                            valueStr = String(val);
                          } else if (Array.isArray(val)) {
                            type = 'array';
                            valueStr = JSON.stringify(val);
                          } else if (typeof val === 'object' && val !== null) {
                            type = 'object';
                          } else {
                            type = 'string';
                            valueStr = String(val);
                          }
                          
                          const propIndex = props.length;
                          props.push({
                            name: key,
                            type,
                            required: false,
                            xIdentity: false,
                            level,
                            parent: parentIndex,
                            collapsed: false,
                            value: valueStr
                          });
                          
                          // If it's an object, recursively parse children
                          if (type === 'object') {
                            parseObject(val, level + 1, propIndex);
                          }
                        });
                      };
                      
                      parseObject(parsed, 0);
                      setMetadataSchemaProperties(props);
                    } catch (e) {
                      console.error('Failed to parse metadata JSON:', e);
                    }
                  }}
                    disabled={!metadataSchemaJsonValid}
                    title="Sync visual editor with JSON changes"
                  >
                    <i className="fas fa-sync"></i> Sync Visual
                  </button>
                </label>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setShowMetadataJson(!showMetadataJson)}
                  title={showMetadataJson ? "Hide JSON" : "Show JSON"}
                >
                  <i className={`fas fa-${showMetadataJson ? 'eye-slash' : 'eye'}`}></i> {showMetadataJson ? 'Hide' : 'Show'} JSON
                </button>
              </div>
              {showMetadataJson && (
              <div className={`schema-property-card ${!metadataSchemaJsonValid ? 'border-danger' : metadataSchemaJsonValid && metadataSchemaJson.trim() && metadataSchemaJson.trim() !== '{}' ? 'border-success' : ''}`}>
                <textarea 
                  className={`form-control schema-raw-json ${!metadataSchemaJsonValid ? 'is-invalid' : metadataSchemaJsonValid && metadataSchemaJson.trim() && metadataSchemaJson.trim() !== '{}' ? 'is-valid' : ''}`}
                  id="metadata-schema-raw-json"
                  rows={15}
                  value={metadataSchemaJson}
                  onChange={(e) => handleSchemaChange(e.target.value, false, true)}
                  onInput={(e) => handleSchemaChange((e.target as HTMLTextAreaElement).value, false, true)}
                  onKeyUp={(e) => handleSchemaChange((e.target as HTMLTextAreaElement).value, false, true)}
                  placeholder='Enter custom metadata as JSON (e.g., {"description": "Sales Agent", "version": "1.0"})'
                />
                {!metadataSchemaJsonValid && (
                  <div className="invalid-feedback d-block">
                    <i className="fas fa-exclamation-triangle"></i> 
                    {metadataSchemaValidationErrors.length > 0 ? (
                      <div>
                        {metadataSchemaValidationErrors.map((error, idx) => (
                          <div key={idx}>{error}</div>
                        ))}
                      </div>
                    ) : (
                      'Invalid JSON'
                    )}
                  </div>
                )}
              </div>
              )}
              <small className="form-text text-muted">
                Enter any custom JSON metadata to describe this extension point. This can include properties like description, version, capabilities, etc.
              </small>
            </div>
          </>
        )}
      </div>
      )}
    </div>
    </>
  );
};

export default InboundTab;
